import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    clinicName: '',
    contactPerson: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('clinicName', formData.clinicName);
      formBody.append('contactPerson', formData.contactPerson);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);

      const response = await fetch('https://readdy.ai/api/form/d59cfch3kamldd1566qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ clinicName: '', contactPerson: '', phone: '', email: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/e54d470d-f2fc-426c-a9d8-9dc8c480a3fc.png" 
              alt="Logo" 
              className="h-10 w-auto"
            />
          </div>
          <a 
            href="#demo" 
            className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Solicitar demo
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/7ef4851c2a3b522e451b8b73b5c2893d.jpg" 
            alt="Hero background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Reduce las citas perdidas con recordatorios automáticos por WhatsApp
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-100 max-w-3xl mx-auto leading-relaxed">
            Conecta tu Google Calendar con WhatsApp y recuerda automáticamente las citas a tus pacientes. Menos ausencias, más ingresos.
          </p>
          <a 
            href="#demo" 
            className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
          >
            Solicitar demo gratuita
          </a>
          <p className="mt-4 text-sm text-gray-200">Prueba gratis durante 5 días</p>
        </div>
      </section>

      {/* Problema Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              ¿Tu clínica pierde dinero por citas no atendidas?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Muchas clínicas enfrentan el mismo problema cada día
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-calendar-close-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Pacientes que no se presentan
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Entre el 15% y 30% de las citas programadas terminan en ausencias, generando pérdidas económicas significativas.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-notification-off-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Falta de recordatorios efectivos
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Los pacientes olvidan sus citas cuando no reciben recordatorios oportunos y por el canal adecuado.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-yellow-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-phone-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Llamadas manuales que consumen tiempo
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Tu personal pierde horas valiosas llamando a pacientes para recordar citas, tiempo que podrían dedicar a otras tareas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solución Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              La solución automática que tu clínica necesita
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sistema inteligente de recordatorios por WhatsApp integrado con tu Google Calendar
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://static.readdy.ai/image/0c3c6f5dc8b0048dc91f43de56e63a0d/afdf7155909971e34d5d6b23c5ed1ba6.jpeg" 
                alt="WhatsApp recordatorios" 
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>

            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-whatsapp-line text-2xl text-teal-600"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Recordatorios automáticos por WhatsApp
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Tus pacientes reciben mensajes directamente en WhatsApp, el canal que más utilizan y revisan constantemente.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-calendar-check-line text-2xl text-teal-600"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Integración directa con Google Calendar
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Conecta tu calendario existente sin cambiar tu forma de trabajar. El sistema detecta automáticamente todas las citas.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-message-3-line text-2xl text-teal-600"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Mensajes personalizados
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Cada recordatorio incluye el nombre del paciente, fecha, hora y detalles específicos de su cita.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-settings-3-line text-2xl text-teal-600"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Sistema totalmente automático
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Una vez configurado, funciona sin intervención. Tú te enfocas en atender pacientes, nosotros en recordarles sus citas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cómo funciona Section */}
      <section className="py-24 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Cómo funciona en 3 pasos simples
            </h2>
            <p className="text-xl text-gray-600">
              Implementación rápida y sin complicaciones
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-teal-100 h-full">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                  1
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Agenda tus citas en Google Calendar
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Continúa trabajando como siempre. Registra las citas de tus pacientes en tu Google Calendar con su número de WhatsApp.
                  </p>
                </div>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <i className="ri-arrow-right-line text-3xl text-teal-400"></i>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-teal-100 h-full">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                  2
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    El sistema detecta automáticamente
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Nuestro sistema revisa tu calendario constantemente y detecta todas las citas programadas sin que tengas que hacer nada.
                  </p>
                </div>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <i className="ri-arrow-right-line text-3xl text-teal-400"></i>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-teal-100 h-full">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                3
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Recordatorio enviado por WhatsApp
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  El paciente recibe un mensaje personalizado en WhatsApp 24 horas antes de su cita, reduciendo drásticamente las ausencias.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Beneficios reales para tu clínica
            </h2>
            <p className="text-xl text-gray-600">
              Resultados medibles desde la primera semana
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-xl border border-teal-100">
              <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                Reducción significativa de no-shows
              </h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                Disminuye hasta un 70% las ausencias a citas. Más pacientes atendidos significa más ingresos para tu clínica sin esfuerzo adicional.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-xl border border-teal-100">
              <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                Ahorro de tiempo al personal
              </h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                Elimina las llamadas manuales de recordatorio. Tu equipo puede dedicar ese tiempo a tareas más importantes y a mejorar la atención al paciente.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-xl border border-teal-100">
              <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-smile-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                Mejor experiencia del paciente
              </h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                Los pacientes valoran recibir recordatorios oportunos por WhatsApp. Mejora la percepción de profesionalidad de tu clínica.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-xl border border-teal-100">
              <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-money-euro-circle-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                Más ingresos sin esfuerzo adicional
              </h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                Cada cita atendida es dinero que antes perdías. El retorno de inversión es inmediato y el sistema se paga solo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Precio Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Precio simple y transparente
            </h2>
            <p className="text-xl text-gray-600">
              Sin costes ocultos ni sorpresas
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border-2 border-teal-600 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-center py-6">
              <h3 className="text-2xl font-bold">Plan Profesional</h3>
              <p className="text-teal-100 mt-2">Para clínicas y negocios con citas</p>
            </div>

            <div className="p-10">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-5xl font-bold text-gray-900">49€</span>
                  <span className="text-2xl text-gray-600">/mes</span>
                </div>
                <p className="text-gray-600">Precio orientativo desde</p>
              </div>

              <ul className="space-y-4 mb-10">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 flex-shrink-0"></i>
                  <span className="text-gray-700 text-lg">Recordatorios automáticos por WhatsApp ilimitados</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 flex-shrink-0"></i>
                  <span className="text-gray-700 text-lg">Integración completa con Google Calendar</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 flex-shrink-0"></i>
                  <span className="text-gray-700 text-lg">Mensajes personalizados con datos de la cita</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 flex-shrink-0"></i>
                  <span className="text-gray-700 text-lg">Soporte técnico incluido</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 flex-shrink-0"></i>
                  <span className="text-gray-700 text-lg">Sin permanencia - cancela cuando quieras</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 flex-shrink-0"></i>
                  <span className="text-gray-700 text-lg">Configuración y puesta en marcha incluida</span>
                </li>
              </ul>

              <a 
                href="#demo" 
                className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Solicitar demo gratuita
              </a>
              <p className="text-center text-gray-600 mt-4">Prueba gratis durante 5 días</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Empieza a reducir las ausencias desde esta semana
          </h2>
          <p className="text-xl mb-10 text-teal-50">
            Únete a las clínicas que ya están ahorrando tiempo y aumentando sus ingresos con recordatorios automáticos
          </p>
          <a 
            href="#demo" 
            className="inline-block bg-white text-teal-600 hover:bg-gray-100 px-10 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
          >
            Solicitar demo
          </a>
        </div>
      </section>

      {/* Formulario Section */}
      <section id="demo" className="py-24 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Solicita tu demo gratuita
            </h2>
            <p className="text-xl text-gray-600">
              Completa el formulario y nos pondremos en contacto contigo en menos de 24 horas
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-gray-50 p-8 rounded-xl border border-gray-200" data-readdy-form>
            <div className="space-y-6">
              <div>
                <label htmlFor="clinicName" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la clínica *
                </label>
                <input
                  type="text"
                  id="clinicName"
                  name="clinicName"
                  value={formData.clinicName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                  Persona de contacto *
                </label>
                <input
                  type="text"
                  id="contactPerson"
                  name="contactPerson"
                  placeholder="Tu nombre completo"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono (WhatsApp) *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="+34 600 000 000"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Enviando...' : 'Solicitar demo gratuita'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  ¡Gracias! Hemos recibido tu solicitud. Nos pondremos en contacto contigo pronto.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  Ha ocurrido un error. Por favor, inténtalo de nuevo o contáctanos directamente.
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/e54d470d-f2fc-426c-a9d8-9dc8c480a3fc.png" 
                alt="Logo" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Solución profesional de recordatorios automáticos por WhatsApp para clínicas y negocios con citas.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Contacto</h4>
              <div className="space-y-3">
                <a href="tel:+34679602857" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-phone-line text-lg"></i>
                  <span className="text-sm">679 602 857</span>
                </a>
                <a href="mailto:smarttechsolutions30@gmail.com" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-mail-line text-lg"></i>
                  <span className="text-sm">smarttechsolutions30@gmail.com</span>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Información</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Recordatorios automáticos</li>
                <li>Integración con Google Calendar</li>
                <li>Soporte incluido</li>
                <li>Sin permanencia</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Todos los derechos reservados</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
